import React, { Component, Context, ContextType, PropsWithChildren } from 'react';

import { WithSnackbarProps, withSnackbar } from 'notistack';
import { LegacyContext } from '~/stormpath/components/LegacyContextType';
import { AuthContext } from '~/stormpath/components/Router';

import { Box, Button, MenuItem, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';

import { withTenant } from '~/components/hoc';

import {
  BOOKING_REFUND_CREDIT_TYPE,
  CREATE_TYPE_PEOPLE_AND_CULTURE,
  CREATE_TYPE_REACTIVATED_CREDIT,
  CREATE_TYPE_TRANSFERRING_CREDIT,
  CREDIT_TYPES_WITHOUT_TICKET_ID,
  CREDIT_TYPE_LEBT_ISSUED,
  CREDIT_TYPE_STORE_CREDIT,
  CREDIT_TYPE_STRIPE_INVOICE,
  ONE_MONTH_EXPIRY,
  STRIPE_PAYMENT_ZONES,
  TWELVE_MONTH_EXPIRY,
} from '~/consts/credits';
import { ROLE_TOUR_COORDINATOR } from '~/consts/roles';

import { createCredit } from '~/services/PaymentsService';
import { addMonths, endOfDay, formatDateLongISO } from '~/services/TimeService';

import { isAdmin } from '~/utils/adminPermission';

import DateTimeWidget from '../Elements/DateTimeWidget';

type Props = {
  currency: string;
  creditTypes: Array<{ creditType: string; label?: string }>;
  id_user: string;
  tenant: App.Tenant;
  updateAllExpiryDate: (date: string) => string;
  rerender: () => void;
};

type State = {
  isAdmin: boolean;
  creditType: string;
  stripe_payment_zones: Array<string>;
  amount: string;
  comments: string;
  bookingId: string;
  ticketId: string;
  cardNumber: string;
  stripePaymentZone: string;
  stripePaymentReference: string;
  showBookingId: boolean;
  showTicketId: boolean;
  showCardNumber: boolean;
  showPaymentZone: boolean;
  showPaymentReference: boolean;
  expires_at: string;
  addCreditsRequestState: Utils.FetchingState;
};

class CreditsForm extends Component<PropsWithChildren<Props & WithSnackbarProps>, State> {
  static contextType: Context<LegacyContext> = AuthContext;
  context: ContextType<typeof AuthContext>;

  currency: string;

  constructor(props: Props & WithSnackbarProps, context) {
    super(props);

    // dayjs.tz.setDefault(window.configs.DEFAULT_TIMEZONE);

    const creditType = '';

    this.state = {
      isAdmin: isAdmin(context.user) || context.user?.roles?.includes(ROLE_TOUR_COORDINATOR),
      creditType: creditType,
      stripe_payment_zones: STRIPE_PAYMENT_ZONES,
      amount: '',
      comments: '',
      bookingId: '',
      ticketId: '',
      cardNumber: '',
      stripePaymentZone: STRIPE_PAYMENT_ZONES[0],
      stripePaymentReference: '',
      showBookingId: false,
      showTicketId: false,
      showCardNumber: false,
      showPaymentZone: false,
      showPaymentReference: false,
      expires_at: this.getExpireDate(),
      addCreditsRequestState: 'idle',
    };
  }

  getExpireDate = (value?: string) => {
    let expiry = TWELVE_MONTH_EXPIRY;

    switch (value) {
      case CREATE_TYPE_REACTIVATED_CREDIT:
      case CREATE_TYPE_PEOPLE_AND_CULTURE:
        // Defaults to 1 month
        expiry = ONE_MONTH_EXPIRY;
        break;
      case CREDIT_TYPE_LEBT_ISSUED:
        // Expire in 3 years
        expiry = 3 * TWELVE_MONTH_EXPIRY;
        break;
    }

    return formatDateLongISO(endOfDay(addMonths(expiry)));
  };

  handleBookingIdChange = (event) => {
    this.setState({ bookingId: event.target.value });
  };

  handleTicketIdChange = (event) => {
    this.setState({ ticketId: event.target.value });
  };

  handleCardNumberChange = (event) => {
    this.setState({ cardNumber: event.target.value });
  };

  handleAddCreditChange = (event) => {
    this.setState({ amount: event.target.value });
  };

  handlePaymentZoneChange = (event) => {
    this.setState({ stripePaymentZone: event.target.value });
  };

  handlePaymentReferenceChange = (event) => {
    this.setState({ stripePaymentReference: event.target.value });
  };

  handleCreditTypeChange = (event) => {
    const value = event.target.value;
    let showBookingId = false;
    let showCardNumber = false;
    let showPaymentZone = false;
    let showPaymentReference = false;
    let showTicketId = false;

    if (value === BOOKING_REFUND_CREDIT_TYPE) {
      showBookingId = true;
    }

    if (value === CREATE_TYPE_TRANSFERRING_CREDIT) {
      showCardNumber = true;
    }

    const paymentZoneTypes = [CREDIT_TYPE_STRIPE_INVOICE, CREDIT_TYPE_STORE_CREDIT];
    if (paymentZoneTypes.includes(value)) {
      showPaymentReference = true;
    }

    if (value === CREDIT_TYPE_STRIPE_INVOICE) {
      showPaymentZone = true;
    }

    if (value && !CREDIT_TYPES_WITHOUT_TICKET_ID.includes(value)) {
      showTicketId = true;
    }

    this.setState({
      creditType: value,
      showBookingId,
      showTicketId,
      showCardNumber,
      showPaymentZone,
      showPaymentReference,
      expires_at: this.getExpireDate(value),
    });
  };

  handleCommentsChange = (event) => {
    this.setState({ comments: event.target.value });
  };

  handleAddCreditSubmit = async (event) => {
    event.preventDefault();

    if (!this.state.creditType) {
      this.props.enqueueSnackbar('Please select a credit type', { variant: 'error' });
      return;
    }

    if (this.state.showTicketId && !this.state.ticketId) {
      this.props.enqueueSnackbar('Please enter a Ticket ID', { variant: 'error' });
      return;
    }

    this.setState({ addCreditsRequestState: 'loading' });

    const object = {
      amount: this.state.amount,
      currency: this.props.currency,
      fk_member: this.props.id_user,
      credit_type: this.state.creditType,
      comments: this.state.comments,
      expires_at: this.state.expires_at,
      brand: this.props.tenant.brand,
    };

    if (this.state.creditType === BOOKING_REFUND_CREDIT_TYPE && this.state.bookingId) {
      object.comments += (object.comments.length ? ' - ' : '') + this.state.bookingId;
    }

    if (this.state.showTicketId && this.state.ticketId) {
      object.comments += (object.comments.length ? ' - ' : '') + 'Ticket ID: ' + this.state.ticketId;
    }

    if (this.state.creditType === CREATE_TYPE_TRANSFERRING_CREDIT && this.state.cardNumber) {
      object.comments += (object.comments.length ? ' - ' : '') + this.state.cardNumber;
    }

    if (
      this.state.creditType === CREDIT_TYPE_STRIPE_INVOICE &&
      this.state.stripePaymentZone &&
      this.state.stripePaymentReference
    ) {
      object.comments +=
        (object.comments.length ? ' - ' : '') +
        'Payment Zone: ' +
        this.state.stripePaymentZone +
        ', Payment Reference: ' +
        this.state.stripePaymentReference;
    }

    try {
      await createCredit(object);
    } catch (error) {
      const errorMessage = error.errors?.[0] || error.message || 'Unknown error';
      this.props.enqueueSnackbar(`Error adding credits: ${errorMessage}`, { variant: 'error' });
      this.setState({ addCreditsRequestState: 'failed' });
      return;
    }

    this.setState({
      amount: '',
      comments: '',
      bookingId: '',
      ticketId: '',
      creditType: '',
      showBookingId: false,
      showTicketId: false,
      showCardNumber: false,
      showPaymentZone: false,
      showPaymentReference: false,
      expires_at: this.getExpireDate(),
      addCreditsRequestState: 'idle',
    });

    this.props.updateAllExpiryDate(object.expires_at);

    this.props.enqueueSnackbar(`Credits added successfully`, { variant: 'success' });

    this.props.rerender();
  };

  setExpiryDate = (expiryDate: string) => {
    this.setState({ expires_at: expiryDate });
  };

  render() {
    return (
      <form onSubmit={this.handleAddCreditSubmit} className="add-credit-form">
        <Grid spacing={2} container>
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <TextField
              id="AddCredits"
              label={`Amount in ${this.props.currency}`}
              variant="outlined"
              type="number"
              value={this.state.amount}
              onChange={this.handleAddCreditChange}
              fullWidth
              required
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <TextField
              label="Credit type"
              variant="outlined"
              value={this.state.creditType}
              onChange={this.handleCreditTypeChange}
              select
              fullWidth
            >
              {this.props.creditTypes
                .sort((a, b) => (a.label || a.creditType).localeCompare(b.label || b.creditType))
                .map((item, idx) => (
                  <MenuItem key={idx} value={item.creditType}>
                    {item.label || item.creditType}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <DateTimeWidget
              label="Expiry date"
              value={this.state.expires_at}
              onChange={this.setExpiryDate}
              disabled={!this.state.isAdmin}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              label="Comment"
              placeholder="Additional comment, why this credits were added"
              variant="outlined"
              value={this.state.comments}
              onChange={this.handleCommentsChange}
              fullWidth
              multiline
            />
          </Grid>

          {this.state.showBookingId && (
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <TextField
                label="Related Order ID"
                placeholder=""
                variant="outlined"
                value={this.state.bookingId}
                onChange={this.handleBookingIdChange}
                inputProps={{
                  minLength: 6,
                }}
                helperText="e.g. 8f5f1c5a-7e88-4fcc-b07e-c6d9a2c58ab1"
                fullWidth
                required
              />
            </Grid>
          )}

          {this.state.showTicketId && (
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <TextField
                label="Ticket ID"
                placeholder=""
                variant="outlined"
                value={this.state.ticketId}
                onChange={this.handleTicketIdChange}
                fullWidth
                required
              />
            </Grid>
          )}

          {this.state.showCardNumber && (
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <TextField
                id="cardNumber"
                label="Case number with approval"
                placeholder="Case number with approval"
                variant="outlined"
                value={this.state.cardNumber}
                onChange={this.handleCardNumberChange}
                inputProps={{
                  minLength: 2,
                }}
                fullWidth
                required
              />
            </Grid>
          )}

          {this.state.showPaymentZone && (
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <TextField
                label="Stripe Payment Zone"
                variant="outlined"
                value={this.state.stripePaymentZone}
                onChange={this.handlePaymentZoneChange}
                select
                fullWidth
                required
              >
                {this.state.stripe_payment_zones.map((item, idx) => (
                  <MenuItem key={idx} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          {this.state.showPaymentReference && (
            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <TextField
                label="Payment Reference"
                variant="outlined"
                value={this.state.stripePaymentReference}
                onChange={this.handlePaymentReferenceChange}
                inputProps={{
                  minLength: 2,
                }}
                fullWidth
                required
              />
            </Grid>
          )}
        </Grid>
        <Box textAlign="right" mt={2}>
          <Button variant="contained" type="submit" disabled={this.state.addCreditsRequestState === 'loading'}>
            Add credits
          </Button>
        </Box>
      </form>
    );
  }
}

export default withTenant(withSnackbar(CreditsForm));

export { CreditsForm as CreditsFormComponent };
